import {
  IresDeviceRequest,
  IresDeviceRequestType,
  PanelRequestStatus,
} from "@/data/models/PanelRequest";
import * as base64Buffer from "base64-arraybuffer";
import {
  savePanelRequest as saveIresDeviceRequest,
  getIresDeviceRequestsRef,
} from "@/data/repository/PanelRequestRepository";
import { IresDeviceFrameBuilder } from "@/utils/IresDeviceFrameBuilder";
import { ControlConfig, ComBoxSensorUsage } from "@/data/models/ControlConfig";
import { hasCapabilityForVersion, DeviceCapability } from "@/data/models/DeviceCapability";
import { calculateIndexFromSlotNumber } from "@/data/models/ComBox";
import {
  ALLOWED_FACTORY_RESET_FLAGS,
  needsRestartAfterFactoryReset,
} from "@/utils/combox/ComBoxFactoryReset";

async function clearQueuedUserRequest(serialNumber: string) {
  const result = await getIresDeviceRequestsRef(serialNumber)
    .where("canBeObserved", "==", true)
    .get();

  return Promise.all(result.docs.map((doc) => doc.ref.delete()));
}

async function saveComBoxRequests(
  serialNumber: string,
  ...iresDeviceRequests: IresDeviceRequest[]
) {
  await clearQueuedUserRequest(serialNumber);

  return Promise.all(
    iresDeviceRequests.map((request) => saveIresDeviceRequest(request))
  );
}

function buildRequestId(
  serialNumber: string,
  iresDeviceRequestType: IresDeviceRequestType
) {
  return `${serialNumber}_${iresDeviceRequestType}_USER_ACTION`;
}

function buildRequest(
  serialNumber: string,
  payload: string,
  requestType: IresDeviceRequestType,
  priority?: number
): IresDeviceRequest {
  return {
    requestId: buildRequestId(serialNumber, requestType),
    priority: priority ?? 500,
    canBeObserved: true,
    panelId: serialNumber,
    payload: payload,
    requestStatus: PanelRequestStatus.PENDING,
    forwardImmediately: true,
    createdAt: new Date(),
    requestType: requestType,
  };
}

export async function createPowerOffRequest(serialNumber: string) {
  const builder = new IresDeviceFrameBuilder(8);
  const frame = builder.toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_POWER_OFF;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

export async function createPowerOnRequest(
  serialNumber: string,
  meterReading: number
) {
  const builder = new IresDeviceFrameBuilder(7);
  builder.addUint32(Math.round(meterReading * 100));

  const frame = builder.toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_POWER_ON;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

export async function createResumeComBoxCommand(serialNumber: string) {
  const frame = new IresDeviceFrameBuilder(3).toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_RESUME;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

export async function createTerminateComBoxDryRunCommand(serialNumber: string) {
  const frame = new IresDeviceFrameBuilder(4).toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_TERMINATE_DRY_RUN;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

function buildRestartRequest(serialNumber: string, priority: number) {
  const frame = new IresDeviceFrameBuilder(35).toIresDeviceFrame();

  return buildRequest(serialNumber, frame, IresDeviceRequestType.RESTART, priority);
}

export async function createRestartComBoxCommand(serialNumber: string) {
  return saveComBoxRequests(serialNumber, buildRestartRequest(serialNumber, 10000));
}

export async function createTestRelaysCommando(
  serialNumber: string,
  numCycles: number,
  onOffDuration: number,
  relay1Active: boolean,
  relay2Active: boolean
) {
  const builder = new IresDeviceFrameBuilder(104);

  builder.addUint8(numCycles);
  builder.addUint8(onOffDuration);

  const relay1ActiveFlag = relay1Active ? 1 : 0;
  const relay2ActiveFlag = relay2Active ? 1 : 0;

  builder.addUint8(relay1ActiveFlag + 2 * relay2ActiveFlag);

  const frame = builder.toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_TEST_RELAYS;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

export async function createPauseComBoxCommand(
  serialNumber: string,
  pauseDuratinInMin: number,
  forcePause: boolean
) {
  const builder = new IresDeviceFrameBuilder(9);

  builder.addUint8(pauseDuratinInMin);
  builder.addBoolean(forcePause);

  const frame = builder.toIresDeviceFrame();
  const requestType = IresDeviceRequestType.COM_BOX_PAUSE;

  return saveComBoxRequests(
    serialNumber,
    buildRequest(serialNumber, frame, requestType)
  );
}

function buildStartComBoxFrame(
  offsetToUtc: number,
  controlConfig: ControlConfig,
  firmwareVersion: string,
  serialNumber: string
) {
  const builder = new IresDeviceFrameBuilder(1);

  builder.addUint8(controlConfig.socket1Mode + controlConfig.socket2Mode * 4);
  builder.addUint8(controlConfig.dryingMode);

  builder.addUint8(
    controlConfig.dryingProgram + controlConfig.sensorProgram * 16
  );

  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.SENSOR_USAGE_INFO)) {
    let toAdd = 0;

    controlConfig.sensorUsage.slice(0, 4).forEach((usage, index) => {
      let flag = 0;

      if (usage === ComBoxSensorUsage.MEASUREMENT) {
        flag = 1;
      } else if (usage === ComBoxSensorUsage.PROGRAM) {
        flag = 2;
      } else if (usage === ComBoxSensorUsage.REFERENCE) {
        flag = 3;
      }

      toAdd += flag << (index * 2);
    });

    builder.addUint8(toAdd);
  } else {
    builder.addBitField(
      controlConfig.sensorUsage.map(
        (usage) => usage == ComBoxSensorUsage.PROGRAM
      )
    );
  }

  builder.addUint8(
    (controlConfig.nightModeActive ? 1 : 0) +
      (controlConfig.nightModeAffectsAllSockets ? 1 : 0) * 2
  );

  builder.addUint8((240 + controlConfig.nightModeStartHour - offsetToUtc) % 24);
  builder.addUint8(controlConfig.nightModeStartMinute);

  builder.addUint8((240 + controlConfig.nightModeEndHour - offsetToUtc) % 24);
  builder.addUint8(controlConfig.nightModeEndMinute);

  builder.addBitField(controlConfig.nightModeActiveDays);

  /**
   * ==================== FIRMWARE v4.0.0+ CHANGES ====================
   * For firmware ≥ 4.0.0, append 5 bytes at the end.
   * - The first byte is 'bytesFollowing' with value 4, indicating the number of subsequent bytes.
   * - The next 4 bytes encode all 9 sensors (slots 1 through 9) in slot order, with the room sensor (slot 9) encoded last.
   * - The usage for the room sensor is read from index 4 in the Firestore array but encoded into the last (9th) position in the bitfield.
   * - This structure maintains backward compatibility with earlier firmware versions and ensures consistent frontend data structure expectations.
   */
  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.EXTENDED_SENSORS)) {
    // Add 1 byte for bytesFollowing = 4
    builder.addUint8(4);

    let bitField = 0;
    // For firmware >= 4.0.0, encode sensors in slot order: 1,2,3,4,5,6,7,8,9 (room last)
    for (let slotNo = 1; slotNo <= 9; slotNo++) {
      const idx = calculateIndexFromSlotNumber(slotNo);
      const usage = controlConfig.sensorUsage[idx];
      let flag = 0;
      if (usage === ComBoxSensorUsage.MEASUREMENT) flag = 1;
      else if (usage === ComBoxSensorUsage.PROGRAM) flag = 2;
      else if (usage === ComBoxSensorUsage.REFERENCE) flag = 3;
      bitField |= flag << ((slotNo - 1) * 2);
    }

    for (let i = 0; i < 4; i++) {
      builder.addUint8((bitField >> (i * 8)) & 0xff);
    }
  }

  return builder;
}

export async function createStartComBoxRequest(
  serialNumber: string,
  offsetToUtc: number,
  controlConfig: ControlConfig,
  startNewDryRun: boolean,
  firmwareVersion: string
) {
  const builderWithOffset = buildStartComBoxFrame(
    offsetToUtc,
    controlConfig,
    firmwareVersion,
    serialNumber
  );
  const frame = builderWithOffset.toIresDeviceFrame();

  const builderWithoutOffset = buildStartComBoxFrame(
    0,
    controlConfig,
    firmwareVersion,
    serialNumber
  );

  const dataBase64 = base64Buffer.encode(builderWithoutOffset.rawPayload);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const request: any = buildRequest(
    serialNumber,
    frame,
    IresDeviceRequestType.COM_BOX_START_DRY_RUN
  );

  request.requestType = startNewDryRun
    ? IresDeviceRequestType.COM_BOX_START_DRY_RUN
    : IresDeviceRequestType.COM_BOX_CHANGE_DRY_RUN;

  request.dataBase64 = dataBase64;
  request.nightModeActive = controlConfig.nightModeActive;

  return saveComBoxRequests(serialNumber, request);
}

export async function createGetConfigsRequest(serialNumber: string) {
  const getBaseConfigRequestFrame = "AREQEHMiBA";
  const getBaseConfigRequest = buildRequest(
    serialNumber,
    getBaseConfigRequestFrame,
    IresDeviceRequestType.COM_BOX_GET_CLIENT_CONFIG
  );

  getBaseConfigRequest.priority = 298;

  getBaseConfigRequest.panelId = serialNumber;
  getBaseConfigRequest.canBeObserved = false;

  await saveIresDeviceRequest(getBaseConfigRequest);

  /*
  const getClientConfigRequestFrame = "AREQEHMiBA";
  const getClientConfigRequest = buildRequest(
    serialNumber,
    getClientConfigRequestFrame,
    IresDeviceRequestType.COM_BOX_GET_CLIENT_CONFIG
  );

  getClientConfigRequest.priority = 298;

  getClientConfigRequest.panelId = serialNumber;
  getClientConfigRequest.canBeObserved = false;

  await saveIresDeviceRequest(getClientConfigRequest);
  */
}

/** The command 102 frame: the request id followed by the flags byte. */
export function buildComBoxFactoryResetFrame(flags: number): string {
  if (flags === 0 || (flags & ~ALLOWED_FACTORY_RESET_FLAGS) !== 0) {
    throw new Error(`Unsupported factory reset flags: ${flags}`);
  }

  const builder = new IresDeviceFrameBuilder(102);
  builder.addUint8(flags);

  return builder.toIresDeviceFrame();
}

/**
 * Queues a factory reset for a ComBox or Mini. The firmware accepts it only in standby, idle or
 * malfunction, so a running drying is ended first and the box switched to idle; on a box that is
 * already idle those two steps are answered with "invalid state" and change nothing. A datalog
 * reset is followed by a NEUSTART, without which the box records no run. All of it is queued at
 * once, because a later user action would delete what is still queued; the requests go out
 * highest priority first, in one connection.
 *
 * IRES-only and temporary: the place for device debugging actions is IRES Admin's device management.
 */
export async function createFactoryResetRequest(
  serialNumber: string,
  flags: number
) {
  const factoryResetFrame = buildComBoxFactoryResetFrame(flags);

  const terminateRequest = buildRequest(
    serialNumber,
    new IresDeviceFrameBuilder(4).toIresDeviceFrame(),
    IresDeviceRequestType.COM_BOX_TERMINATE_DRY_RUN,
    300
  );

  // Command 13, which the firmware calls NEW_DRY_RUN: moves a terminated box to idle.
  const switchToIdleRequest = buildRequest(
    serialNumber,
    new IresDeviceFrameBuilder(13).toIresDeviceFrame(),
    IresDeviceRequestType.COM_BOX_SWITCH_TO_IDLE,
    299
  );

  const factoryResetRequest = buildRequest(
    serialNumber,
    factoryResetFrame,
    IresDeviceRequestType.COM_BOX_RESET,
    298
  );

  const requests = [terminateRequest, switchToIdleRequest, factoryResetRequest];

  if (needsRestartAfterFactoryReset(flags)) {
    requests.push(buildRestartRequest(serialNumber, 297));
  }

  return saveComBoxRequests(serialNumber, ...requests);
}
